import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, RotateCcw } from "lucide-react";
import { iconOf } from "../lib/icons";
import { session } from "../lib/storage";

const ROLES = {
  actor: { title: "الممثل", icon: "drama", committee: "لجنة التمثيل والتحدث المسرحي", text: "الأضواء تعرفك قبل أن تعرفها. تعيش الشخصية وتجعل الجمهور ينسى أنه في قاعة." },
  director: { title: "المخرج", icon: "clapperboard", committee: "لجنة التنظيم والإخراج", text: "ترى العرض كاملاً قبل أن يبدأ، وتعرف كيف تحرّك كل تفصيلة لتصنع لحظة لا تُنسى." },
  writer: { title: "كاتب النصوص", icon: "pen", committee: "لجنة النصوص والتدريب", text: "قبل أن تُرفع الستارة، كانت الحكاية كلها في رأسك. الكلمة عندك أقوى من أي إضاءة." },
  media: { title: "الموثّق", icon: "camera", committee: "لجنة التوثيق والإعلام", text: "عينك تلتقط اللحظة التي يفوّتها الجميع، وتجعل العرض يعيش بعد انتهائه." },
  designer: { title: "المصمم", icon: "palette", committee: "لجنة التصميم وكتابة المحتوى", text: "الهوية، الألوان، البوستر الذي يوقف الناس في الممر… كلها بصمتك." },
};

const QUESTIONS = [
  { q: "الستارة سترتفع بعد دقيقة. أين أنت؟", a: [["على الخشبة أتنفس بعمق", "actor"], ["خلف الكواليس أتابع كل شيء", "director"], ["في الزاوية أراجع النص للمرة العاشرة", "writer"]] },
  { q: "تعطّلت الإضاءة فجأة في منتصف العرض!", a: [["أرتجل حتى تعود", "actor"], ["أعطي إشارة للفريق وأعيد الترتيب", "director"], ["أصوّر اللحظة… ستكون حكاية جميلة", "media"]] },
  { q: "أُعطيت ورقة فارغة قبل البروفة…", a: [["أكتب مشهداً جديداً", "writer"], ["أرسم البوستر", "designer"], ["أكتب خطة البروفة والأدوار", "director"]] },
  { q: "أجمل لحظة بعد العرض؟", a: [["تصفيق الجمهور", "actor"], ["مراجعة الصور والفيديو مع الفريق", "media"], ["رؤية تصميمي على كل الجدران", "designer"]] },
];

const KEY = "theater_egg_seen_v1";

export default function EasterEgg({ enabled = true }) {
  const [stage, setStage] = useState("hidden"); // hidden | tease | game | result
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({});
  const [clicks, setClicks] = useState(0);

  const open = useCallback(() => { setStage("tease"); session.set(KEY, "1"); }, []);

  useEffect(() => {
    if (!enabled) return;
    const onScroll = () => {
      if (session.get(KEY)) return;
      const bottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 80;
      if (bottom) open();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled, open]);

  useEffect(() => {
    if (!enabled) return;
    const onLogo = () => setClicks((c) => { if (c + 1 >= 5) { setStage("tease"); return 0; } return c + 1; });
    const el = document.querySelector("[data-testid='nav-logo-image']");
    el?.addEventListener("click", onLogo);
    return () => el?.removeEventListener("click", onLogo);
  }, [enabled]);

  const pick = (role) => {
    const next = { ...scores, [role]: (scores[role] || 0) + 1 };
    setScores(next);
    if (step + 1 < QUESTIONS.length) setStep(step + 1);
    else setStage("result");
  };
  const restart = () => { setScores({}); setStep(0); setStage("game"); };
  const close = () => setStage("hidden");
  const winner = Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || "actor";
  const R = ROLES[winner];
  const Icon = iconOf(R.icon);

  if (stage === "hidden") return null;

  return (
    <AnimatePresence>
      <motion.div data-testid="easter-egg" className="fixed inset-0 z-[120] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/85" onClick={close} />
        <motion.div className="absolute inset-y-0 left-0 w-1/2 bg-[#5a1f1c] curtain-texture" initial={{ x: 0 }} animate={{ x: "-100%" }} transition={{ duration: 1.1, ease: [0.7, 0, 0.3, 1], delay: 0.15 }} />
        <motion.div className="absolute inset-y-0 right-0 w-1/2 bg-[#5a1f1c] curtain-texture" initial={{ x: 0 }} animate={{ x: "100%" }} transition={{ duration: 1.1, ease: [0.7, 0, 0.3, 1], delay: 0.15 }} />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(240,232,219,0.22),transparent_55%)]" />

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="relative w-full max-w-lg theater-card rounded-3xl p-7 sm:p-9 border border-[#F0E8DB]/40 text-center"
        >
          <button data-testid="easter-egg-close" onClick={close} className="absolute top-4 left-4 text-[#BFB3A0] hover:text-[#F0E8DB]"><X size={20} /></button>

          {stage === "tease" && (
            <>
              <Sparkles className="text-[#F0E8DB] mx-auto mb-3" size={28} />
              <div className="text-[#F0E8DB]/60 text-xs tracking-[0.3em] font-serif-en mb-2">PSST…</div>
              <h3 className="text-[#F0E8DB] text-2xl sm:text-3xl font-bold">لحظة! قبل أن تغادر…</h3>
              <p className="text-[#C5B8A8] mt-3 leading-relaxed">قرأت الحكاية كلها، بقي سؤال واحد: <span className="text-[#F0E8DB]">وش دورك أنت على الخشبة؟</span><br />٤ أسئلة سريعة ونقول لك.</p>
              <div className="flex gap-3 justify-center mt-6">
                <button data-testid="easter-egg-start" onClick={() => setStage("game")} className="btn-gold rounded-full px-6 py-2.5 text-sm font-semibold">ارفعوا الستارة</button>
                <button data-testid="easter-egg-skip" onClick={close} className="btn-ghost rounded-full px-5 py-2.5 text-sm">مرة ثانية</button>
              </div>
            </>
          )}

          {stage === "game" && (
            <>
              <div className="flex justify-center gap-1.5 mb-5">
                {QUESTIONS.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i <= step ? "w-8 bg-[#F0E8DB]" : "w-4 bg-[#F0E8DB]/25"}`} />)}
              </div>
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
                <h3 data-testid="easter-egg-question" className="text-[#F0E8DB] text-xl sm:text-2xl font-bold leading-snug">{QUESTIONS[step].q}</h3>
                <div className="mt-6 space-y-2.5">
                  {QUESTIONS[step].a.map(([label, role], i) => (
                    <button key={label} data-testid={`easter-egg-answer-${i}`} onClick={() => pick(role)} className="w-full text-right rounded-xl border border-[#F0E8DB]/25 bg-[#241615] hover:bg-[#7A2E2A]/50 hover:border-[#F0E8DB]/70 transition-colors px-4 py-3 text-[#F0E8DB] text-sm">
                      {label}
                    </button>
                  ))}
                </div>
              </motion.div>
            </>
          )}

          {stage === "result" && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} data-testid="easter-egg-result">
              <div className="w-20 h-20 rounded-full mx-auto bg-gradient-to-br from-[#7A2E2A] to-[#3d1614] border-2 border-[#F0E8DB] gold-glow flex items-center justify-center mb-4">
                <Icon size={34} className="text-[#F0E8DB]" />
              </div>
              <div className="text-[#F0E8DB]/60 text-xs tracking-[0.3em] font-serif-en mb-1">YOUR ROLE</div>
              <h3 className="text-[#F0E8DB] text-3xl font-bold">أنت {R.title}</h3>
              <p className="text-[#C5B8A8] mt-3 leading-relaxed">{R.text}</p>
              <div className="mt-4 inline-block px-4 py-1.5 rounded-full border border-[#F0E8DB]/40 text-[#F0E8DB] text-xs">مكانك: {R.committee}</div>
              <div className="flex gap-3 justify-center mt-6">
                <a data-testid="easter-egg-join" href="#contact" onClick={close} className="btn-gold rounded-full px-6 py-2.5 text-sm font-semibold">انضم لهذه اللجنة</a>
                <button data-testid="easter-egg-restart" onClick={restart} className="btn-ghost rounded-full px-4 py-2.5 text-sm inline-flex items-center gap-1"><RotateCcw size={14} /> أعدها</button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
